import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import "../assets/css/studentdashboard.css";
import PortalGuideModal from "../components/PortalGuideModal";

const API =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_BASE_URL || "https://schools.luth.gov.ng"
    : "http://localhost:3003";

const GUIDE_STORAGE_KEY = "portal_guide_dismissed_v1";

const getCurrentAcademicSession = () => {
  const year = new Date().getFullYear();
  return `${year}/${year + 1}`;
};

const gradeFromScore = (score) => {
  const n = Number(score);
  if (Number.isNaN(n)) return { grade: "N/A", points: 0 };
  if (n >= 70) return { grade: "A", points: 4 };
  if (n >= 60) return { grade: "B", points: 3 };
  if (n >= 50) return { grade: "C", points: 2 };
  if (n >= 40) return { grade: "D", points: 1 };
  // if (n >= 40) return { grade: "E", points: 1 };
  return { grade: "F", points: 0 };
};

const safeText = (value, fallback = "N/A") =>
  value === null || value === undefined || String(value).trim() === ""
    ? fallback
    : value;

const LoadingChip = ({ loading }) =>
  loading ? <span className="loading-chip">Loading data…</span> : null;

const StatCard = ({ title, value, subtitle, tone = "default" }) => (
  <div className={`stat-card ${tone}`}>
    <div className="stat-top">
      <span className="stat-title">{title}</span>
      <span className="stat-value">{value}</span>
    </div>
    {subtitle ? <p className="stat-subtitle">{subtitle}</p> : null}
  </div>
);

const ActionCard = ({
  title,
  description,
  buttonText,
  onClick,
  tone = "default",
  badge,
}) => (
  <div className={`action-card ${tone}`}>
    <div className="action-head">
      <div>
        <h3>{title}</h3>
        {badge ? <span className="action-badge">{badge}</span> : null}
      </div>
    </div>

    <p>{description}</p>

    <button type="button" className="action-button" onClick={onClick}>
      {buttonText}
    </button>
  </div>
);



export default function StudentsDashboard() {
  const navigate = useNavigate();

  const [student, setStudent] = useState({
    photo: "",
    name: "Student",
    matricNo: safeText(localStorage.getItem("matricNo"), "N/A"),
    regNo: "N/A",
    email: "",
    phoneNumber: "",
    department: safeText(localStorage.getItem("programme"), "N/A"),
    programme: safeText(localStorage.getItem("programme"), "N/A"),
    level: safeText(localStorage.getItem("level"), "N/A"),
    session: safeText(localStorage.getItem("session"), "N/A"),
    academicSession: getCurrentAcademicSession(),
    entryMode: "UTME",
    entryYear: "N/A",
  });

  const [registeredCourses, setRegisteredCourses] = useState([]);
  const [results, setResults] = useState([]);
  const [payments, setPayments] = useState([]);
  const [registeredUnits, setRegisteredUnits] = useState(0);
  const [maxUnits, setMaxUnits] = useState(0);
  const [feeAmount, setFeeAmount] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [showGuide, setShowGuide] = useState(false);

  const token = localStorage.getItem("token");
  const matricNo = localStorage.getItem("matricNo");

  useEffect(() => {
    const dismissed = localStorage.getItem(GUIDE_STORAGE_KEY) === "1";
    if (!dismissed) {
      setShowGuide(true);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const currentAcademicSession = getCurrentAcademicSession();
        const programmeName = localStorage.getItem("programme") || "N/A";
        const savedLevel = localStorage.getItem("level") || "N/A";
        const savedSession = localStorage.getItem("session") || "N/A";

        if (!matricNo) {
          setError("Matriculation number not found. Please log in again.");
          return;
        }

        const requests = await Promise.allSettled([
          axios.get(`${API}/api/users/${matricNo}`, { headers }),
          axios.get(`${API}/api/biodata/${matricNo}`, { headers }),
          axios.get(`${API}/api/courses/registered`, {
            headers,
            params: {
              matricNo,
              level: savedLevel,
              session: savedSession,
              academicSession: currentAcademicSession,
            },
          }),
          axios.get(`${API}/api/courses/maxUnits`, {
            headers,
            params: {
              programmeName,
              level: savedLevel,
              semester: savedSession,
            },
          }),
          axios.get(`${API}/api/payments/history`, {
            headers,
            params: { matricNo },
          }),
          axios.get(`${API}/api/results/view`, {
            headers,
            params: { matricNo },
          }),
        ]);

        const user =
          requests[0].status === "fulfilled"
            ? requests[0].value.data || {}
            : {};
        const bio =
          requests[1].status === "fulfilled"
            ? requests[1].value.data || {}
            : {};
        const regCourses =
          requests[2].status === "fulfilled"
            ? requests[2].value.data || []
            : [];
        const maxUnitsData =
          requests[3].status === "fulfilled"
            ? requests[3].value.data || {}
            : {};
        const payHist =
          requests[4].status === "fulfilled"
            ? requests[4].value.data || []
            : [];
        const gradeData =
          requests[5].status === "fulfilled"
            ? requests[5].value.data || []
            : [];

        const level =
          user.StudentLevel ||
          bio.level ||
          localStorage.getItem("level") ||
          "N/A";

        const session = bio.session || localStorage.getItem("session") || "N/A";

        const academicSession =
          user.CalenderYear || bio.CalenderYear || currentAcademicSession;

        const programmeNameFromApi =
          localStorage.getItem("programme") ||
          bio.department ||
          user.programme ||
          "N/A";

        if (cancelled) return;

        setStudent({
          photo: user.picture || bio.picture || "",
          name: user.fullName || bio.fullName || "Student",
          matricNo: user.matricNo || bio.matricNumber || matricNo,
          regNo: bio.jambRegNumber || "N/A",
          email: user.email || bio.email || "",
          phoneNumber: bio.phoneNumber || "",
          department: user.programme || bio.department || "N/A",
          programme: programmeNameFromApi,
          level: level || "N/A",
          session: session || "N/A",
          academicSession: academicSession || "N/A",
          entryMode: user.entryMode || bio.entryMode || "UTME",
          entryYear: user.entryYear || bio.entryYear || "N/A",
        });

        setRegisteredCourses(Array.isArray(regCourses) ? regCourses : []);
        setRegisteredUnits(
          Array.isArray(regCourses)
            ? regCourses.reduce((sum, c) => sum + Number(c.CreditUnits || 0), 0)
            : 0,
        );
        setMaxUnits(Number(maxUnitsData?.totalUnits || 0));
        setFeeAmount(Number(maxUnitsData?.feeAmount || 0));
        setPayments(Array.isArray(payHist) ? payHist : []);
        setResults(Array.isArray(gradeData) ? gradeData : []);
        setError("");
      } catch (e) {
        console.error("Dashboard load error:", e);
        if (!cancelled) setError("Some dashboard data could not be loaded.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [matricNo, token]);

  const totalPaymentsVal = useMemo(() => {
    return (Array.isArray(payments) ? payments : []).reduce((sum, payment) => {
      const amt =
        payment.Amount ??
        payment.amount ??
        payment.PaidAmount ??
        payment.paidAmount ??
        0;
      return sum + Number(amt || 0);
    }, 0);
  }, [payments]);

  const totalFees = Number(feeAmount || 0);
  const totalOutstanding = Math.max(totalFees - totalPaymentsVal, 0);
  const unusedUnits = Math.max(
    Number(maxUnits || 0) - Number(registeredUnits || 0),
    0,
  );

  const { cgpa, cgpaTotalUnits } = useMemo(() => {
    let totalUnits = 0;
    let totalGradePoints = 0;

    for (const r of results) {
      const score = r.Score == null ? null : Number(r.Score);
      const computed = gradeFromScore(score);
      const points =
        r.Points != null && r.Points !== ""
          ? Number(r.Points)
          : computed.points;
      const units = Number(r.CreditUnits ?? 0) || 0;

      if (!Number.isFinite(units) || units <= 0) continue;

      totalUnits += units;
      totalGradePoints += points * units;
    }

    const val = totalUnits > 0 ? totalGradePoints / totalUnits : 0;
    return { cgpa: Number(val.toFixed(2)), cgpaTotalUnits: totalUnits };
  }, [results]);

  const semesterSummary = useMemo(() => {
    const selectedLevel = student.level;
    const selectedSemester = student.session;

    const filtered = (Array.isArray(results) ? results : [])
      .filter((r) =>
        selectedLevel && selectedLevel !== "N/A"
          ? String(r.CourseLevel) === String(selectedLevel)
          : true,
      )
      .filter((r) =>
        selectedSemester && selectedSemester !== "N/A"
          ? String(r.CourseSemester) === String(selectedSemester)
          : true,
      );

    let totalUnits = 0;
    let totalGradePoints = 0;

    for (const r of filtered) {
      const units = Number(r.CreditUnits ?? 0) || 0;
      const score = r.Score == null ? null : Number(r.Score);
      const { points } = gradeFromScore(score);
      if (!Number.isFinite(units) || units <= 0) continue;

      totalUnits += units;
      totalGradePoints += points * units;
    }

    const gpa = totalUnits > 0 ? totalGradePoints / totalUnits : 0;

    return {
      semesterGPA: Number(gpa.toFixed(2)),
      semesterTotalUnits: totalUnits,
    };
  }, [results, student.level, student.session]);

  const closeGuide = () => {
    setShowGuide(false);
    localStorage.setItem(GUIDE_STORAGE_KEY, "1");
  };

  const hideGuideForever = () => {
    localStorage.setItem(GUIDE_STORAGE_KEY, "1");
    setShowGuide(false);
  };

  return (
    <div
      className="student-dashboard"
      style={{ marginTop: "-130px", paddingTop: "0px" }}
    >
      {" "}
      <Sidebar />
      <div className="dashboard-main">
        <div className="dashboard-top">
          <div className="dashboard-hero">
            <div className="hero-photo-wrap">
              {student.photo ? (
                <img src={student.photo} alt="Student" className="hero-photo" />
              ) : (
                <div className="hero-photo placeholder">No Photo</div>
              )}
            </div>

            <div className="hero-text">
              <div className="hero-row">
                <div>
                  <p className="hero-label">Welcome back</p>
                  <h1 className="hero-name">{student.name}</h1>
                </div>

                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <LoadingChip loading={loading} />
                  <button
                    type="button"
                    className="guide-open-btn"
                    onClick={() => setShowGuide(true)}
                  >
                    How to use portal
                  </button>
                </div>
              </div>

              <div className="hero-meta">
                <span>
                  <strong>Matric No:</strong> {safeText(student.matricNo)}
                </span>
                <span>
                  <strong>Programme:</strong> {safeText(student.programme)}
                </span>
                <span>
                  <strong>Level:</strong> {safeText(student.level)}
                </span>
                <span>
                  <strong>Session:</strong> {safeText(student.session)}
                </span>
              </div>

              {error ? <p className="dashboard-error">{error}</p> : null}
            </div>
          </div>

          <div className="summary-grid">
            <StatCard
              title="Registered Courses"
              value={registeredCourses.length || 0}
              subtitle="Courses added for this session"
              tone="blue"
            />
            <StatCard
              title="Registered Units"
              value={registeredUnits || 0}
              subtitle="Total credit units registered"
              tone="green"
            />
            <StatCard
              title="Max Units"
              value={maxUnits || 0}
              subtitle="Allowed maximum units"
              tone="purple"
            />
            <StatCard
              title="Unused Units"
              value={unusedUnits || 0}
              subtitle="Still available to register"
              tone="orange"
            />
            <StatCard
              title="CGPA"
              value={Number.isFinite(cgpa) ? cgpa.toFixed(2) : "0.00"}
              subtitle={`Based on ${cgpaTotalUnits || 0} units`}
              tone="indigo"
            />
            <StatCard
              title="Semester GPA"
              value={
                Number.isFinite(semesterSummary.semesterGPA)
                  ? semesterSummary.semesterGPA.toFixed(2)
                  : "0.00"
              }
              subtitle={`Semester units: ${semesterSummary.semesterTotalUnits || 0}`}
              tone="teal"
            />
          </div>
        </div>

        <div className="major-area-grid">
          <ActionCard
            title="Registered Courses"
            description="View the courses you have already registered, along with their credit units and course details."
            buttonText="Open Courses"
            onClick={() => navigate("/registered-courses")}
            tone="blue"
            badge={`${registeredCourses.length || 0} courses`}
          />

          <ActionCard
            title="Check Results"
            description="Open your results page to view grades, GPA, CGPA, and semester performance."
            buttonText="View Results"
            onClick={() => navigate("/results")}
            tone="green"
            badge={`CGPA ${Number.isFinite(cgpa) ? cgpa.toFixed(2) : "0.00"}`}
          />

          <ActionCard
            title="Pay Fees"
            description="Review fees, payment history, outstanding balance, and payment status."
            buttonText="Open Fees"
            onClick={() => navigate("/pay-fees")}
            tone="orange"
            badge={`Outstanding ${totalOutstanding.toLocaleString()}`}
          />

          <ActionCard
            title="Edit Profile"
            description="Update your biodata, passport photo, and other profile details."
            buttonText="Edit Profile"
            onClick={() => navigate("/student-profile")}
            tone="purple"
            badge="Profile"
          />
        </div>

        <div className="details-grid">
          <div className="details-card">
            <div className="card-head">
              <h2>Units Overview</h2>
              <span className="muted-pill">Live summary</span>
            </div>

            <div className="mini-stat-row">
              <div className="mini-stat">
                <span>Max Units</span>
                <strong>{maxUnits || 0}</strong>
              </div>
              <div className="mini-stat">
                <span>Registered</span>
                <strong>{registeredUnits || 0}</strong>
              </div>
              <div className="mini-stat">
                <span>Unused</span>
                <strong>{unusedUnits || 0}</strong>
              </div>
            </div>
          </div>

          <div className="details-card">
            <div className="card-head">
              <h2>Fee Summary</h2>
              <span className="muted-pill">Payments</span>
            </div>

            <div className="mini-stat-row">
              <div className="mini-stat">
                <span>Total Fees</span>
                <strong>{totalFees.toLocaleString()}</strong>
              </div>
              <div className="mini-stat">
                <span>Paid</span>
                <strong>{totalPaymentsVal.toLocaleString()}</strong>
              </div>
              <div className="mini-stat">
                <span>Outstanding</span>
                <strong>{totalOutstanding.toLocaleString()}</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="table-card">
          <div className="card-head">
            <h2>Recent Registered Courses</h2>
            <span className="muted-pill">
              {registeredCourses.length || 0} total
            </span>
          </div>

          {registeredCourses.length === 0 ? (
            <p className="empty-state">
              No registered courses found yet. The dashboard is still available.
            </p>
          ) : (
            <div className="table-wrap">
              <table className="dashboard-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Code</th>
                    <th>Title</th>
                    <th>Level</th>
                    <th>Semester</th>
                    <th>Units</th>
                  </tr>
                </thead>
                <tbody>
                  {registeredCourses.slice(0, 6).map((course, index) => (
                    <tr key={course.CourseID || course.id || index}>
                      <td>{index + 1}</td>
                      <td>{course.CourseCode || "N/A"}</td>
                      <td>{course.CourseTitle || course.Title || "N/A"}</td>
                      <td>{course.CourseLevel || student.level || "N/A"}</td>
                      <td>
                        {course.CourseSemester || student.session || "N/A"}
                      </td>
                      <td>{course.CreditUnits || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {showGuide ? (
        <PortalGuideModal
          onClose={closeGuide}
          onHideForever={hideGuideForever}
        />
      ) : null}
    </div>
  );
}
